import { DIFFICULTY_CONFIG } from '../config/difficulty.js';
import { playSound } from '../systems/audio.js';
import { getFirstGamepad, isButtonPressed, getAxisValue } from '../config/controls.js';
import { GAMEPAD_BUTTONS, GAMEPAD_AXES } from '../config/controls.js';
import { showCustomization, hideCustomization } from './customization.js';
import { loadPlayerCustomizations } from '../systems/storage.js';
import { isTouchDevice } from '../systems/touch.js';

// Menu state
let menuPhase = 'players'; // 'players', 'difficulty', or 'customize'
let selectedPlayerCount = 1;
let selectedDifficulty = 'normal';
let menuActive = false;

// Navigation state (to prevent repeat)
let navCooldown = 0;
let selectCooldown = 0;

// Reference to selectCurrent function (set by startMenuInput)
let selectCurrentFn = null;

// Create and show the main menu
export function showMainMenu(onStart) {
    menuActive = true;
    menuPhase = 'players';
    selectedPlayerCount = 1;
    selectedDifficulty = 'normal';

    const menuContainer = document.createElement('div');
    menuContainer.id = 'main-menu';
    menuContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        font-family: 'Arial', sans-serif;
    `;

    menuContainer.innerHTML = `
        <div id="menu-title" style="
            font-size: 64px;
            font-weight: bold;
            color: #fff;
            text-shadow: 0 0 20px #ff6b6b, 0 0 40px #ff6b6b, 0 0 60px #ff6b6b;
            margin-bottom: 60px;
            animation: titleGlow 2s ease-in-out infinite alternate;
        ">BOSS BATTLE</div>

        <div id="menu-content" style="
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
        "></div>

        <div id="menu-controls" style="
            position: absolute;
            bottom: 30px;
            color: #888;
            font-size: 14px;
            text-align: center;
        ">
            ${isTouchDevice() ? `
                <div>Tap options to select</div>
            ` : `
                <div>Navigate: Arrow Keys / WASD / D-Pad</div>
                <div>Select: SPACE / ENTER / A Button</div>
            `}
        </div>
    `;

    document.body.appendChild(menuContainer);

    // Add CSS animation
    addMenuStyles();

    // Render initial phase
    renderPlayerSelection();

    // Start input handling
    startMenuInput(menuContainer, onStart);
}

// Add menu CSS styles
function addMenuStyles() {
    if (document.getElementById('menu-styles')) return;

    const style = document.createElement('style');
    style.id = 'menu-styles';
    style.textContent = `
        @keyframes titleGlow {
            from { text-shadow: 0 0 20px #ff6b6b, 0 0 40px #ff6b6b; }
            to { text-shadow: 0 0 30px #feca57, 0 0 60px #feca57, 0 0 80px #feca57; }
        }

        .menu-option {
            padding: 20px 60px;
            font-size: 24px;
            font-weight: bold;
            color: #aaa;
            background: rgba(255, 255, 255, 0.05);
            border: 3px solid transparent;
            border-radius: 15px;
            cursor: pointer;
            transition: all 0.2s ease;
            min-width: 300px;
            text-align: center;
            -webkit-tap-highlight-color: transparent;
            touch-action: manipulation;
            user-select: none;
            -webkit-user-select: none;
        }

        .menu-option:hover {
            background: rgba(255, 255, 255, 0.1);
            color: #fff;
        }

        .menu-option:active {
            transform: scale(0.98);
        }

        .menu-option.selected {
            background: linear-gradient(135deg, #ff6b6b 0%, #feca57 100%);
            color: #fff;
            border-color: #fff;
            transform: scale(1.05);
            box-shadow: 0 0 30px rgba(255, 107, 107, 0.5);
        }

        /* Touch device optimizations */
        @media (hover: none) and (pointer: coarse) {
            .menu-option {
                padding: 25px 60px;
                font-size: 22px;
                min-height: 70px;
            }
        }

        .menu-subtitle {
            font-size: 28px;
            color: #feca57;
            margin-bottom: 30px;
            text-transform: uppercase;
            letter-spacing: 3px;
        }

        .menu-description {
            font-size: 14px;
            color: #888;
            margin-top: 5px;
        }
    `;
    document.head.appendChild(style);
}

// Render player count selection
function renderPlayerSelection() {
    const content = document.getElementById('menu-content');
    if (!content) return;

    // On touch devices, skip player selection (only 1 player supported)
    if (isTouchDevice()) {
        selectedPlayerCount = 1;
        menuPhase = 'difficulty';
        renderDifficultySelection();
        return;
    }

    content.innerHTML = `
        <div class="menu-subtitle">How Many Players?</div>

        <div class="menu-option ${selectedPlayerCount === 1 ? 'selected' : ''}" data-players="1">
            1 PLAYER
            <div class="menu-description">Solo adventure</div>
        </div>

        <div class="menu-option ${selectedPlayerCount === 2 ? 'selected' : ''}" data-players="2">
            2 PLAYERS
            <div class="menu-description">Co-op mode (share keyboard or use controllers)</div>
        </div>
    `;

    // Add click handlers
    content.querySelectorAll('.menu-option').forEach(option => {
        option.addEventListener('click', () => {
            const players = parseInt(option.dataset.players);
            selectedPlayerCount = players;
            playSound('menuSelect');
            menuPhase = 'difficulty';
            renderDifficultySelection();
        });
    });
}

// Render difficulty selection
function renderDifficultySelection() {
    const content = document.getElementById('menu-content');
    if (!content) return;

    const difficulties = Object.entries(DIFFICULTY_CONFIG);

    content.innerHTML = `
        <div class="menu-subtitle">Choose Difficulty</div>

        ${difficulties.map(([key, config]) => `
            <div class="menu-option ${selectedDifficulty === key ? 'selected' : ''}" data-difficulty="${key}">
                ${config.name.toUpperCase()}
                <div class="menu-description">${config.description}</div>
            </div>
        `).join('')}
    `;

    // Add click handlers
    content.querySelectorAll('.menu-option').forEach(option => {
        option.addEventListener('click', () => {
            selectedDifficulty = option.dataset.difficulty;
            // Proceed to customization/game start
            if (selectCurrentFn) {
                selectCurrentFn();
            }
        });
    });
}

// Start menu input handling
function startMenuInput(menuContainer, onStart) {
    let keyboardHandler;
    let updateLoop;

    const cleanup = () => {
        if (keyboardHandler) {
            document.removeEventListener('keydown', keyboardHandler);
        }
        if (updateLoop) {
            cancelAnimationFrame(updateLoop);
        }
        menuActive = false;
    };

    const selectCurrent = () => {
        playSound('menuSelect');

        if (menuPhase === 'players') {
            menuPhase = 'difficulty';
            renderDifficultySelection();
        } else if (menuPhase === 'difficulty') {
            // Move to customization
            cleanup();
            menuContainer.style.transition = 'opacity 0.3s';
            menuContainer.style.opacity = '0';
            setTimeout(() => {
                menuContainer.remove();
                // Show customization for each player
                showCustomization(1, selectedPlayerCount, (customizations) => {
                    if (customizations === null) {
                        // User cancelled, go back to menu
                        showMainMenu(onStart);
                    } else {
                        // Start the game with customizations
                        onStart({
                            playerCount: selectedPlayerCount,
                            difficulty: selectedDifficulty,
                            customizations: customizations
                        });
                    }
                });
            }, 300);
        }
    };

    // Store reference for click handlers
    selectCurrentFn = selectCurrent;

    const navigate = (direction) => {
        playSound('menuMove');

        if (menuPhase === 'players') {
            selectedPlayerCount = selectedPlayerCount === 1 ? 2 : 1;
            renderPlayerSelection();
        } else {
            const difficulties = Object.keys(DIFFICULTY_CONFIG);
            const currentIndex = difficulties.indexOf(selectedDifficulty);
            const newIndex = (currentIndex + direction + difficulties.length) % difficulties.length;
            selectedDifficulty = difficulties[newIndex];
            renderDifficultySelection();
        }
    };

    const goBack = () => {
        // On touch devices, there's no player selection to go back to
        if (menuPhase === 'difficulty' && !isTouchDevice()) {
            playSound('menuMove');
            menuPhase = 'players';
            renderPlayerSelection();
        }
    };

    // Keyboard input
    keyboardHandler = (e) => {
        if (!menuActive) return;

        switch(e.code) {
            case 'ArrowUp':
            case 'KeyW':
                navigate(-1);
                break;
            case 'ArrowDown':
            case 'KeyS':
                navigate(1);
                break;
            case 'Space':
            case 'Enter':
                selectCurrent();
                break;
            case 'Escape':
            case 'Backspace':
                goBack();
                break;
        }
    };

    document.addEventListener('keydown', keyboardHandler);

    // Gamepad input (polling)
    const pollGamepad = () => {
        if (!menuActive) return;

        navCooldown = Math.max(0, navCooldown - 16);
        selectCooldown = Math.max(0, selectCooldown - 16);

        const pad = getFirstGamepad();
        if (pad) {
            const yAxis = getAxisValue(pad, GAMEPAD_AXES.LEFT_STICK_Y);
            const dpadUp = isButtonPressed(pad, GAMEPAD_BUTTONS.DPAD_UP);
            const dpadDown = isButtonPressed(pad, GAMEPAD_BUTTONS.DPAD_DOWN);
            const aButton = isButtonPressed(pad, GAMEPAD_BUTTONS.A);
            const bButton = isButtonPressed(pad, GAMEPAD_BUTTONS.B);

            if (navCooldown === 0) {
                if (yAxis < -0.5 || dpadUp) {
                    navigate(-1);
                    navCooldown = 200;
                } else if (yAxis > 0.5 || dpadDown) {
                    navigate(1);
                    navCooldown = 200;
                }
            }

            if (selectCooldown === 0) {
                if (aButton) {
                    selectCurrent();
                    selectCooldown = 300;
                } else if (bButton) {
                    goBack();
                    selectCooldown = 300;
                }
            }
        }

        updateLoop = requestAnimationFrame(pollGamepad);
    };

    updateLoop = requestAnimationFrame(pollGamepad);
}

// Hide main menu
export function hideMainMenu() {
    const menu = document.getElementById('main-menu');
    if (menu) {
        menu.remove();
    }
    menuActive = false;
}

// Check if menu is active
export function isMenuActive() {
    return menuActive;
}
